import React, { useCallback, useEffect, useState } from 'react';
import { getStoredAuth } from '../authStorage';
import './Profile.css';
import { apiUrl } from '../api';

function Profile() {
  const { token } = getStoredAuth();
  const [user, setUser] = useState(null);
  
  // Modal & Form State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form, setForm] = useState({ username: '', email: '', phone: '', city: '', locality: '' });
  
  // Verification Tracking States for Modal (phone verification disabled for now)
  const [isEmailVerifiedInSession, setIsEmailVerifiedInSession] = useState(false);
  const [emailVerifyingLink, setEmailVerifyingLink] = useState(null);
  
  const [message, setMessage] = useState('');
  const [modalMessage, setModalMessage] = useState('');


  const resetForm = useCallback((userData) => {
    const u = userData;
    if (!u) return;
    setForm({
      username: u.username || '',
      email: u.email || '',
      phone: u.phone || '',
      city: u.city || '',
      locality: u.locality || ''
    });
    setIsEmailVerifiedInSession(false);
    setEmailVerifyingLink(null);
    setModalMessage('');
  }, []);

  
  const fetchProfile = useCallback(async () => {
    try {
      const res = await fetch(apiUrl('/api/auth/profile'), {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch profile');
      const data = await res.json();
      setUser(data.user);
      resetForm(data.user);
    } catch (err) {
      console.error(err);
    }
  }, [resetForm, token]);

  useEffect(() => {
    if (!token) return;
    fetchProfile();
  }, [token, fetchProfile]);

  

  const openModal = () => {
    resetForm(user);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    resetForm(user);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));

    // Reset verification status if user changes input away from original value
    if (name === 'email' && value !== user?.email) {
      setIsEmailVerifiedInSession(false);
    }
  };

  // --- Email Verification Trigger & Simulation ---
  const handleVerifyEmailTrigger = async () => {
    if (!form.email) {
      setModalMessage('Please enter an email address first.');
      return;
    }
    setModalMessage('');
    try {
      const demoLink = apiUrl(`/api/auth/verify-email?email=${encodeURIComponent(form.email)}`);
      setEmailVerifyingLink(demoLink);
      setModalMessage('Click the generated link below to complete Email verification.');
    } catch (err) {
      setModalMessage('Failed to initiate email verification.');
    }
  };

  const handleConfirmEmailVerification = () => {
    setIsEmailVerifiedInSession(true);
    setEmailVerifyingLink(null);
    setModalMessage('Email verified successfully! You can now save your changes.');
  };

  // --- Save Submit Logic ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    setModalMessage('');

    const emailChanged = form.email !== user.email;
    // Check verification constraints
    if (emailChanged && !isEmailVerifiedInSession) {
      setModalMessage('Please click "Verify Email" to verify your new email address before saving.');
      return;
    }

    try {
      const res = await fetch(apiUrl('/api/auth/profile'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          ...form,
          emailVerified: emailChanged ? isEmailVerifiedInSession : user.verified,
          phoneVerified: true
        })
      });

      const data = await res.json();
      if (!res.ok) {
        setModalMessage(data.error || 'Update failed');
        return;
      }

      if (data.user) {
        setUser(data.user);
        resetForm(data.user);
      }

      setMessage(data.message || 'Profile updated successfully!');
      setIsModalOpen(false);
      if (!data.user) fetchProfile();
    } catch (err) {
      console.error(err);
      setModalMessage('Failed to save profile changes.');
    }
  };

  if (!token) {
    return (
      <div className="profile-container">
        <div className="profile-card auth-warning">
          <div className="hud-icon">🔒</div>
          <p>Please log in to view your profile.</p>
        </div>
      </div>
    );
  }

  const emailHasChanged = form.email !== user?.email;

  return (
    <div className="profile-container">
      <div className="profile-card">
        {/* Profile Header */}
        <div className="profile-header">
          <div className="avatar-orb">
            <span>{user?.username ? user.username.charAt(0).toUpperCase() : 'U'}</span>
            <div className="orb-ring"></div>
          </div>
          <div className="header-info">
            <h2>{user?.username || 'User Profile'}</h2>
            <div className="status-indicator">
              <span className="dot"></span> Account Active
            </div>
          </div>
        </div>

        {user ? (
          <>
            {/* User Details Grid */}
            <div className="profile-grid">
              <div className="grid-item">
                <span className="label">Username</span>
                <span className="value">{user.username}</span>
              </div>

              <div className="grid-item">
                <span className="label">Location</span>
                <span className="value">
                  {user.city || user.locality 
                    ? `${user.city || ''}${user.city && user.locality ? ', ' : ''}${user.locality || ''}` 
                    : '—'}
                </span>
              </div>

              <div className="grid-item">
                <span className="label">Ad Credits</span>
                <span className="value">{user.ad_credits ?? 0} remaining</span>
              </div>

              {/* Email Address */}
              <div className="grid-item full-width">
                <span className="label">Email Address</span>
                <div className="value-with-icon">
                  <span className="value">{user.email}</span>
                  {user.verified ? (
                    <span className="status-badge verified" title="Email Verified">
                      <svg className="badge-icon" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Verified
                    </span>
                  ) : (
                    <span className="status-badge unverified" title="Email Unverified">
                      <svg className="badge-icon" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      Unverified
                    </span>
                  )}
                </div>
              </div>

              {/* Mobile Phone */}
              <div className="grid-item full-width">
                <span className="label">Phone / Mobile</span>
                <div className="value-with-icon">
                  <span className="value">{user.phone || '—'}</span>
                  {user.phone ? (
                    <span className="status-badge Unverified" title="Mobile Unerified">
                      <svg className="badge-icon" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Unverified
                    </span>
                  ) : (
                    <span className="status-badge unverified" title="Mobile Missing">
                      <svg className="badge-icon" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      Unverified
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Action Trigger */}
            <div className="action-row">
              <button className="edit-btn" onClick={openModal}>
                Edit Profile
              </button>
            </div>

            {message && <div className="profile-message">{message}</div>}
          </>
        ) : (
          <div className="loading-state">
            <div className="cyber-spinner"></div>
            <p>Loading user profile...</p>
          </div>
        )}
      </div>

      {/* ==========================================================================
          EDIT PROFILE MODAL POPUP
          ========================================================================== */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Profile</h3>
              <button className="modal-close-btn" onClick={closeModal}>&times;</button>
            </div>

            <form className="modal-form" onSubmit={handleSubmit}>
              {/* Username Input */}
              <div className="input-field-group">
                <label htmlFor="modal-username">Username</label>
                <input
                  id="modal-username"
                  name="username"
                  type="text"
                  value={form.username}
                  onChange={handleChange}
                  placeholder="Enter username"
                  required
                />
              </div>

              {/* Email Input & Verify Button */}
              <div className="input-field-group">
                <label htmlFor="modal-email">Email Address</label>
                <input
                  id="modal-email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="Enter email"
                  required
                />
                
                <div className="verification-action-bar">
                  {!emailHasChanged ? (
                    <span className="field-status-text confirmed">Current Email Verified</span>
                  ) : isEmailVerifiedInSession ? (
                    <span className="field-status-badge verified">✓ New Email Verified</span>
                  ) : (
                    <button
                      type="button"
                      className="inline-verify-btn"
                      onClick={handleVerifyEmailTrigger}
                    >
                      Verify Email
                    </button>
                  )}
                </div>

                {/* Email Verification Link Simulation */}
                {emailVerifyingLink && !isEmailVerifiedInSession && (
                  <div className="verification-inline-panel">
                    <span className="verify-hint">Demo Link generated:</span>
                    <div className="verify-link-text">{emailVerifyingLink}</div>
                    <button
                      type="button"
                      className="confirm-verification-btn"
                      onClick={handleConfirmEmailVerification}
                    >
                      Simulate Link Verification
                    </button>
                  </div>
                )}
              </div>

              {/* Mobile Input (OTP verification disabled) */}
              <div className="input-field-group">
                <label htmlFor="modal-phone">Phone / Mobile</label>
                <input
                  id="modal-phone"
                  name="phone"
                  type="text"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="Enter mobile number"
                />
              </div>

              {/* City Input */}
              <div className="input-field-group">
                <label htmlFor="modal-city">City</label>
                <input
                  id="modal-city"
                  name="city"
                  type="text"
                  value={form.city}
                  onChange={handleChange}
                  placeholder="Enter city"
                />
              </div>

              {/* Locality Input */}
              <div className="input-field-group">
                <label htmlFor="modal-locality">Locality</label>
                <input
                  id="modal-locality"
                  name="locality"
                  type="text"
                  value={form.locality}
                  onChange={handleChange}
                  placeholder="Enter locality"
                />
              </div>

              {/* Modal Messages */}
              {modalMessage && <div className="modal-message">{modalMessage}</div>}

              {/* Modal Form Actions */}
              <div className="modal-actions">
                <button type="submit" className="save-btn">
                  Save Changes
                </button>
                <button type="button" className="cancel-btn" onClick={closeModal}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Profile;